import { isJobActive, isJobFailure } from '@/lib/api'
import type { Job, JobState } from '@/types/domain'

/**
 * The reading of the job list the Verwaltung panel needs: which bucket a job
 * is in, what its row says, and when the list is worth polling.
 *
 * The bucket rules come from `@/lib/api/jobs` rather than being written again
 * here — in particular `isJobFailure`, which counts only `failed`. A cancelled
 * job is terminal and carries the error text "Cancelled", but nothing went
 * wrong: somebody pressed stop. Counting it among the Problemfälle would
 * report a problem for every deliberate cancellation.
 */

export type JobFilterValue = 'alle' | 'aktiv' | 'probleme'

export interface JobCounts {
  alle: number
  aktiv: number
  probleme: number
}

export function jobCounts(jobs: Job[]): JobCounts {
  return {
    alle: jobs.length,
    aktiv: jobs.filter(isJobActive).length,
    probleme: jobs.filter(isJobFailure).length,
  }
}

export function filterJobs(jobs: Job[], filter: JobFilterValue): Job[] {
  if (filter === 'aktiv') return jobs.filter(isJobActive)
  if (filter === 'probleme') return jobs.filter(isJobFailure)
  return jobs
}

export const JOB_STATUS: Record<JobState, { word: string; icon: string; className: string }> = {
  pending: { word: 'wartet', icon: 'schedule', className: 'text-text-muted' },
  processing: { word: 'läuft', icon: 'autorenew', className: 'text-warning-text' },
  completed: { word: 'fertig', icon: 'check_circle', className: 'text-success-text' },
  failed: { word: 'fehlgeschlagen', icon: 'error', className: 'text-danger-text' },
  // Its own status, drawn in the neutral tone: a stopped job is not a failure.
  cancelled: { word: 'abgebrochen', icon: 'cancel', className: 'text-text-muted' },
}

/** A status this build does not know is shown as the gateway spelled it,
 *  rather than crashing the row or being silently folded into another. */
export function jobStatusView(status: JobState): { word: string; icon: string; className: string } {
  return JOB_STATUS[status] ?? { word: String(status), icon: 'help', className: 'text-text-muted' }
}

/**
 * The progress cell.
 *
 * Only a running or finished job has a percentage worth reading: the worker
 * writes `progress` as it goes and leaves it at 0 until it starts, so showing
 * "0 %" for a queued job would look like work that is stuck rather than work
 * that has not begun. A failed or cancelled job's last percentage says nothing
 * about what was stored, so the cell stays empty there too.
 */
export function progressLabel(job: Job): string {
  if (job.status === 'processing' || job.status === 'completed') {
    const value = typeof job.progress === 'number' ? job.progress : 0
    return `${Math.round(Math.min(100, Math.max(0, value)))} %`
  }
  return '–'
}

/**
 * When the job started, or — for one that has not — when it was queued.
 *
 * Time alone for today, date and time for anything older: the table is read
 * as "what is happening right now", and a bare "09:12" on a job from last week
 * would be read as this morning.
 */
export function startedLabel(job: Job, now: Date = new Date()): string {
  const raw = job.started_at ?? job.created_at
  if (!raw) return '–'
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return '–'
  const time = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(date)
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  if (sameDay) return time
  const day = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' }).format(date)
  return `${day} ${time}`
}

/** The id, short enough for the column and long enough to stay unique in a
 *  listing; the full id is the cell's `title`. */
export function shortJobId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id
}

export const POLL_INTERVAL_MS = 4_000
export const HEALTH_INTERVAL_MS = 30_000
/**
 * How long a tab keeps polling on its own.
 *
 * `refetchIntervalInBackground` is off by default, so a hidden tab costs
 * nothing — but a settings tab left open and focused on a second monitor would
 * otherwise poll until the machine is switched off. After the budget the panel
 * says it stopped and the refresh control starts it again.
 */
export const POLL_BUDGET_MS = 15 * 60_000

export function pollInterval(input: {
  active: boolean
  elapsedMs: number
  intervalMs: number
}): number | false {
  if (!input.active) return false
  if (input.elapsedMs >= POLL_BUDGET_MS) return false
  return input.intervalMs
}

/**
 * The template's name for the row, and whether one was found.
 *
 * `GET /v1/admin/jobs` carries `template_id` and no name, so the names come
 * from one listing of the templates — one request for the whole table, never
 * one per row. That listing is capped at 100 by the gateway, so on a large
 * deployment some rows genuinely have no name available; those show the id and
 * the panel says why.
 */
export function templateLabel(
  templateId: string,
  names: Map<string, string>,
): { label: string; named: boolean } {
  const name = names.get(templateId)
  return name ? { label: name, named: true } : { label: templateId, named: false }
}
